import { getToken, clearToken } from '../utils/authToken';
import { useState, useEffect } from 'react';
import { API_URL, API } from '../config';
import { Brain, RefreshCw, Sparkles, Check, X, ChevronRight, Zap, BookOpen, Code, MessageSquare, TrendingUp, History, ToggleLeft, ToggleRight, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import axios from 'axios';


const LearningModePanel = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localPrefs, setLocalPrefs] = useState({});
  const [activeTab, setActiveTab] = useState('preferences'); // 'preferences' | 'intelligent'
  const [intelligentStatus, setIntelligentStatus] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionHistory, setSuggestionHistory] = useState([]);

  const txt = {
    es: {
      title: 'Modo Aprendizaje',
      subtitle: 'DamyxIA aprende tus preferencias de comunicación',
      enabled: 'Activado',
      disabled: 'Desactivado',
      enabledDesc: 'DamyxIA recuerda tus preferencias entre sesiones',
      disabledDesc: 'DamyxIA usa configuración por defecto',
      technicalLevel: 'Nivel Técnico',
      technicalLevelDesc: 'Complejidad del lenguaje usado',
      preferredDepth: 'Profundidad',
      preferredDepthDesc: 'Detalle de las explicaciones',
      preferredTone: 'Tono',
      preferredToneDesc: 'Estilo de comunicación',
      preferredStructure: 'Estructura',
      preferredStructureDesc: 'Formato de las respuestas',
      includeCode: 'Incluir Código',
      includeCodeDesc: 'Mostrar ejemplos de código',
      speedMode: 'Velocidad',
      speedModeDesc: 'Priorizar velocidad o detalle',
      interactions: 'Interacciones',
      interactionsDesc: 'conversaciones analizadas',
      dominantIntent: 'Intención Principal',
      save: 'Guardar Preferencias',
      reset: 'Resetear Perfil',
      resetConfirm: 'Tu perfil de aprendizaje será reseteado',
      saved: 'Preferencias guardadas',
      resetSuccess: 'Perfil reseteado correctamente',
      back: 'Volver',
      loading: 'Cargando perfil...',
      error: 'Error al cargar perfil',
      // Tabs
      preferencesTab: 'Preferencias',
      intelligentTab: 'Perfil Inteligente',
      // Intelligent Profile
      intelligentTitle: 'Perfil Inteligente',
      intelligentDesc: 'Sugerencias automáticas de ajuste',
      intelligentEnabled: 'Auto-ajuste activado',
      intelligentDisabled: 'Auto-ajuste desactivado',
      pendingSuggestions: 'Sugerencias pendientes',
      noSuggestions: 'No hay sugerencias pendientes',
      acceptedCount: 'Aceptadas',
      rejectedCount: 'Rechazadas',
      suggestionHistory: 'Historial',
      accept: 'Aceptar',
      reject: 'Rechazar',
      accepted: 'Aceptada',
      rejected: 'Rechazada',
      clearHistory: 'Limpiar historial',
      // Options
      beginner: 'Principiante',
      intermediate: 'Intermedio',
      advanced: 'Avanzado',
      low: 'Baja',
      medium: 'Media',
      high: 'Alta',
      neutral: 'Neutral',
      formal: 'Formal',
      friendly: 'Amigable',
      direct: 'Directo',
      technical: 'Técnico',
      short: 'Corta',
      long: 'Larga',
      step_by_step: 'Paso a paso',
      bullet_points: 'Puntos',
      fast: 'Rápido',
      detailed: 'Detallado',
      yes: 'Sí',
      no: 'No',
      // Intents
      debug: 'Depuración',
      build: 'Construcción',
      explain: 'Explicación',
      ask_fact: 'Consultas',
      compare: 'Comparación',
    },
    en: {
      title: 'Learning Mode',
      subtitle: 'DamyxIA learns your communication preferences',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enabledDesc: 'DamyxIA remembers your preferences across sessions',
      disabledDesc: 'DamyxIA uses default settings',
      technicalLevel: 'Technical Level',
      technicalLevelDesc: 'Complexity of language used',
      preferredDepth: 'Depth',
      preferredDepthDesc: 'Detail in explanations',
      preferredTone: 'Tone',
      preferredToneDesc: 'Communication style',
      preferredStructure: 'Structure',
      preferredStructureDesc: 'Response format',
      includeCode: 'Include Code',
      includeCodeDesc: 'Show code examples',
      speedMode: 'Speed',
      speedModeDesc: 'Prioritize speed or detail',
      interactions: 'Interactions',
      interactionsDesc: 'conversations analyzed',
      dominantIntent: 'Main Intent',
      save: 'Save Preferences',
      reset: 'Reset Profile',
      resetConfirm: 'Your learning profile will be reset',
      saved: 'Preferences saved',
      resetSuccess: 'Profile reset successfully',
      back: 'Back',
      loading: 'Loading profile...',
      error: 'Error loading profile',
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      neutral: 'Neutral',
      formal: 'Formal',
      friendly: 'Friendly',
      direct: 'Direct',
      technical: 'Technical',
      short: 'Short',
      long: 'Long',
      step_by_step: 'Step by step',
      bullet_points: 'Bullet points',
      fast: 'Fast',
      detailed: 'Detailed',
      yes: 'Yes',
      no: 'No',
      debug: 'Debugging',
      build: 'Building',
      explain: 'Explanation',
      ask_fact: 'Questions',
      compare: 'Comparison',
      // Tabs
      preferencesTab: 'Preferences',
      intelligentTab: 'Smart Profile',
      // Intelligent Profile
      intelligentTitle: 'Smart Profile',
      intelligentDesc: 'Automatic adjustment suggestions',
      intelligentEnabled: 'Auto-adjust enabled',
      intelligentDisabled: 'Auto-adjust disabled',
      pendingSuggestions: 'Pending suggestions',
      noSuggestions: 'No pending suggestions',
      acceptedCount: 'Accepted',
      rejectedCount: 'Rejected',
      suggestionHistory: 'History',
      accept: 'Accept',
      reject: 'Reject',
      accepted: 'Accepted',
      rejected: 'Rejected',
      clearHistory: 'Clear history',
    }
  };

  const t = txt[language] || txt.es;

  useEffect(() => {
    if (isOpen) {
      loadProfile();
      loadOptions();
      loadIntelligentStatus();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUserId = () => {
    const token = getToken();
    if (!token || token === 'guest') return 'guest';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.email || 'guest';
    } catch {
      return 'guest';
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const userId = getUserId();
      const response = await axios.get(`${API}/learning/profile/${userId}`);
      setProfile(response.data);
      setLocalPrefs({
        technical_level: response.data.technical_level,
        preferred_depth: response.data.preferred_depth,
        preferred_tone: response.data.preferred_tone,
        preferred_structure: response.data.preferred_structure,
        include_code: response.data.include_code,
        speed_mode: response.data.speed_mode,
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const response = await axios.get(`${API}/learning/preferences/options`);
      setOptions(response.data);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const loadIntelligentStatus = async () => {
    try {
      const userId = getUserId();
      const [statusRes, suggestionsRes, historyRes] = await Promise.all([
        axios.get(`${API}/intelligent-profile/status/${userId}`),
        axios.get(`${API}/intelligent-profile/suggestions/${userId}`),
        axios.get(`${API}/intelligent-profile/history/${userId}?limit=10`)
      ]);
      setIntelligentStatus(statusRes.data);
      setSuggestions(suggestionsRes.data.suggestions || []);
      setSuggestionHistory(historyRes.data.suggestions || []);
    } catch (error) {
      console.error('Error loading intelligent profile:', error);
    }
  };

  const handleToggleIntelligent = async () => {
    try {
      const userId = getUserId();
      const newEnabled = !intelligentStatus?.enabled;
      await axios.post(`${API}/intelligent-profile/toggle/${userId}`, { enabled: newEnabled });
      setIntelligentStatus(prev => ({ ...prev, enabled: newEnabled }));
      toast.success(newEnabled ? t.intelligentEnabled : t.intelligentDisabled);
    } catch (error) {
      console.error('Error toggling intelligent profile:', error);
    }
  };

  const handleAcceptSuggestion = async (suggestionId) => {
    try {
      const userId = getUserId();
      await axios.post(`${API}/intelligent-profile/accept/${suggestionId}?user_id=${userId}`);
      toast.success(t.saved);
      await Promise.all([loadProfile(), loadIntelligentStatus()]);
    } catch (error) {
      console.error('Error accepting suggestion:', error);
    }
  };

  const handleRejectSuggestion = async (suggestionId) => {
    try {
      const userId = getUserId();
      await axios.post(`${API}/intelligent-profile/reject/${suggestionId}?user_id=${userId}`);
      await loadIntelligentStatus();
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
    }
  };

  const handleClearHistory = async () => {
    try {
      const userId = getUserId();
      await axios.delete(`${API}/intelligent-profile/clear/${userId}`);
      await loadIntelligentStatus();
      toast.success(t.resetSuccess);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  const handleToggleLearning = async () => {
    try {
      const userId = getUserId();
      const newEnabled = !profile.learning_mode_enabled;
      await axios.post(`${API}/learning/profile/${userId}/toggle`, {
        enabled: newEnabled
      });
      setProfile(prev => ({ ...prev, learning_mode_enabled: newEnabled }));
      toast.success(newEnabled ? t.enabled : t.disabled);
    } catch (error) {
      console.error('Error toggling learning mode:', error);
    }
  };

  const handlePrefChange = (key, value) => {
    setLocalPrefs(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = getUserId();
      await axios.put(`${API}/learning/profile/${userId}`, localPrefs);
      await loadProfile();
      toast.success(t.saved);
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t.resetConfirm)) return;
    try {
      const userId = getUserId();
      await axios.delete(`${API}/learning/profile/${userId}`);
      await loadProfile();
      toast.success(t.resetSuccess);
    } catch (error) {
      console.error('Error resetting profile:', error);
    }
  };

  if (!isOpen) return null;

  const iconForLevel = (level) => {
    switch (level) {
      case 'beginner': return '🌱';
      case 'intermediate': return '📈';
      case 'advanced': return '🚀';
      default: return '📈';
    }
  };

  const iconForIntent = (intent) => {
    switch (intent) {
      case 'debug': return '🔧';
      case 'build': return '🏗️';
      case 'explain': return '📚';
      case 'ask_fact': return '❓';
      case 'compare': return '⚖️';
      default: return '💬';
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-[#1a1a2e] border border-purple-500/30 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 px-6 py-4 border-b border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Brain className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{t.title}</h2>
                <p className="text-sm text-gray-400">{t.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab('preferences')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'preferences'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Brain className="w-4 h-4 inline-block mr-2" />
              {t.preferencesTab}
            </button>
            <button
              onClick={() => setActiveTab('intelligent')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'intelligent'
                  ? 'bg-purple-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              {t.intelligentTab}
              {suggestions.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {suggestions.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
              <span className="ml-2 text-gray-400">{t.loading}</span>
            </div>
          ) : activeTab === 'preferences' && profile ? (
            <div className="space-y-6">
              {/* Toggle Learning Mode */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl border border-purple-500/20">
                <div className="flex items-center gap-3">
                  <Sparkles className={`w-5 h-5 ${profile.learning_mode_enabled ? 'text-purple-400' : 'text-gray-500'}`} />
                  <div>
                    <p className="text-white font-medium">
                      {profile.learning_mode_enabled ? t.enabled : t.disabled}
                    </p>
                    <p className="text-sm text-gray-400">
                      {profile.learning_mode_enabled ? t.enabledDesc : t.disabledDesc}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleToggleLearning}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    profile.learning_mode_enabled ? 'bg-purple-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    profile.learning_mode_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-gray-400">{t.interactions}</span>
                  </div>
                  <p className="text-2xl font-bold text-white">{profile.interaction_count}</p>
                  <p className="text-xs text-gray-500">{t.interactionsDesc}</p>
                </div>
                {profile.intent_patterns && Object.keys(profile.intent_patterns).length > 0 && (
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-gray-400">{t.dominantIntent}</span>
                    </div>
                    {(() => {
                      const dominant = Object.entries(profile.intent_patterns)
                        .sort((a, b) => b[1] - a[1])[0];
                      return dominant ? (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{iconForIntent(dominant[0])}</span>
                          <span className="text-lg font-bold text-white">
                            {t[dominant[0]] || dominant[0]}
                          </span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              {/* Preferences */}
              {profile.learning_mode_enabled && options && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                    Preferencias
                  </h3>

                  {/* Technical Level */}
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{t.technicalLevel}</p>
                        <p className="text-xs text-gray-400">{t.technicalLevelDesc}</p>
                      </div>
                      <span className="text-xl">{iconForLevel(localPrefs.technical_level)}</span>
                    </div>
                    <div className="flex gap-2">
                      {options.technical_level?.options.map(opt => (
                        <button
                          key={opt}
                          onClick={() => handlePrefChange('technical_level', opt)}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
                            localPrefs.technical_level === opt
                              ? 'bg-purple-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t[opt] || opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Depth */}
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{t.preferredDepth}</p>
                        <p className="text-xs text-gray-400">{t.preferredDepthDesc}</p>
                      </div>
                      <BookOpen className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex gap-2">
                      {options.preferred_depth?.options.map(opt => (
                        <button
                          key={opt}
                          onClick={() => handlePrefChange('preferred_depth', opt)}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
                            localPrefs.preferred_depth === opt
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t[opt] || opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tone */}
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{t.preferredTone}</p>
                        <p className="text-xs text-gray-400">{t.preferredToneDesc}</p>
                      </div>
                      <MessageSquare className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {options.preferred_tone?.options.map(opt => (
                        <button
                          key={opt}
                          onClick={() => handlePrefChange('preferred_tone', opt)}
                          className={`py-2 px-3 rounded-lg text-sm transition-all ${
                            localPrefs.preferred_tone === opt
                              ? 'bg-green-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t[opt] || opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Structure */}
                  <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">{t.preferredStructure}</p>
                        <p className="text-xs text-gray-400">{t.preferredStructureDesc}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {options.preferred_structure?.options.map(opt => (
                        <button
                          key={opt}
                          onClick={() => handlePrefChange('preferred_structure', opt)}
                          className={`py-2 px-3 rounded-lg text-sm transition-all ${
                            localPrefs.preferred_structure === opt
                              ? 'bg-orange-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t[opt] || opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Include Code & Speed Mode */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-white font-medium text-sm">{t.includeCode}</p>
                          <p className="text-xs text-gray-400">{t.includeCodeDesc}</p>
                        </div>
                        <Code className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePrefChange('include_code', true)}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
                            localPrefs.include_code
                              ? 'bg-cyan-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t.yes}
                        </button>
                        <button
                          onClick={() => handlePrefChange('include_code', false)}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
                            !localPrefs.include_code
                              ? 'bg-cyan-500 text-white'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t.no}
                        </button>
                      </div>
                    </div>

                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-white font-medium text-sm">{t.speedMode}</p>
                          <p className="text-xs text-gray-400">{t.speedModeDesc}</p>
                        </div>
                        <Zap className="w-4 h-4 text-yellow-400" />
                      </div>
                      <div className="flex gap-2">
                        {options.speed_mode?.options.map(opt => (
                          <button
                            key={opt}
                            onClick={() => handlePrefChange('speed_mode', opt)}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
                              localPrefs.speed_mode === opt
                                ? 'bg-yellow-500 text-black'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10'
                            }`}
                          >
                            {t[opt] || opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'intelligent' ? (
            /* Intelligent Profile Tab */
            <div className="space-y-6">
              {/* Toggle Intelligent Profile */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl border border-indigo-500/20">
                <div className="flex items-center gap-3">
                  <Sparkles className={`w-5 h-5 ${intelligentStatus?.enabled ? 'text-indigo-400' : 'text-gray-500'}`} />
                  <div>
                    <p className="text-white font-medium">{t.intelligentTitle}</p>
                    <p className="text-sm text-gray-400">{t.intelligentDesc}</p>
                  </div>
                </div>
                <button
                  onClick={handleToggleIntelligent}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    intelligentStatus?.enabled ? 'bg-indigo-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    intelligentStatus?.enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-white/5 rounded-xl border border-white/10 text-center">
                  <p className="text-xl font-bold text-orange-400">{suggestions.length}</p>
                  <p className="text-xs text-gray-500">{t.pendingSuggestions}</p>
                </div>
                <div className="p-3 bg-white/5 rounded-xl border border-white/10 text-center">
                  <p className="text-xl font-bold text-green-400">{intelligentStatus?.accepted_count || 0}</p>
                  <p className="text-xs text-gray-500">{t.acceptedCount}</p>
                </div>
                <div className="p-3 bg-white/5 rounded-xl border border-white/10 text-center">
                  <p className="text-xl font-bold text-red-400">{intelligentStatus?.rejected_count || 0}</p>
                  <p className="text-xs text-gray-500">{t.rejectedCount}</p>
                </div>
              </div>

              {/* Pending Suggestions */}
              {suggestions.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                    {t.pendingSuggestions}
                  </h3>
                  {suggestions.map(suggestion => (
                    <div key={suggestion.id} className="p-4 bg-gradient-to-r from-orange-500/10 to-yellow-500/10 rounded-xl border border-orange-500/20">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-orange-400" />
                            <span className="text-sm font-medium text-white">
                              {t[suggestion.suggestion_type] || suggestion.suggestion_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-300 mb-2">{suggestion.reason}</p>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="px-2 py-1 rounded bg-red-500/20 text-red-300">
                              {t[suggestion.old_value] || suggestion.old_value}
                            </span>
                            <ChevronRight className="w-3 h-3 text-gray-500" />
                            <span className="px-2 py-1 rounded bg-green-500/20 text-green-300">
                              {t[suggestion.new_value] || suggestion.new_value}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAcceptSuggestion(suggestion.id)}
                            className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                            title={t.accept}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRejectSuggestion(suggestion.id)}
                            className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                            title={t.reject}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center bg-white/5 rounded-xl border border-white/10">
                  <Sparkles className="w-8 h-8 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">{t.noSuggestions}</p>
                </div>
              )}

              {/* History */}
              {suggestionHistory.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                      {t.suggestionHistory}
                    </h3>
                    <button
                      onClick={handleClearHistory}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      {t.clearHistory}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {suggestionHistory.map(item => (
                      <div key={item.id} className="p-3 bg-white/5 rounded-lg border border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">
                            {t[item.suggestion_type] || item.suggestion_type}
                          </span>
                          <span className="text-xs text-gray-500">
                            {t[item.old_value] || item.old_value} → {t[item.new_value] || item.new_value}
                          </span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.status === 'accepted' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {item.status === 'accepted' ? t.accepted : t.rejected}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-purple-500/30 bg-white/5">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t.reset}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !profile?.learning_mode_enabled}
              className="px-6 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {t.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LearningModePanel;
